import React, { useEffect, useId, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import Utils from "../utility/utility";
import { STRING } from "../constants/string";
import TimeField from "./TimeField";
import { useSurface, SurfaceProvider } from "../lib/surface-context";
import { surfaceClasses } from "../lib/surface-classes";

/* react-day-picker ships dist/style.css. It is deliberately not imported:
   this map replaces every class in the rendered tree with one of ours, so the
   .df-cal-* rules in theme.css style the calendar outright and never have to
   out-specify vendor CSS. Keys are react-day-picker v8's ClassNames contract —
   if you upgrade the library, check this map against its types first. */
const calendarClassNames = {
  root: "df-cal",
  months: "df-cal-months",
  month: "df-cal-month",
  caption: "df-cal-caption",
  caption_label: "df-cal-caption-label",
  nav: "df-cal-nav",
  nav_button: "df-cal-nav-btn",
  nav_button_previous: "df-cal-nav-prev",
  nav_button_next: "df-cal-nav-next",
  table: "df-cal-table",
  head_row: "df-cal-head-row",
  head_cell: "df-cal-head-cell",
  row: "df-cal-row",
  cell: "df-cal-cell",
  day: "df-cal-day",
  day_today: "df-cal-day-today",
  day_selected: "df-cal-day-selected",
  day_outside: "df-cal-day-outside",
  day_disabled: "df-cal-day-disabled",
  day_hidden: "df-cal-day-hidden",
  vhidden: "df-cal-vhidden",
};

const Chevron = ({ dir }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <polyline points={dir === "left" ? "15 18 9 12 15 6" : "9 18 15 12 9 6"} />
  </svg>
);

const CalendarGlyph = () => (
  <svg
    className="df-trigger-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <line x1="8" y1="3" x2="8" y2="7" />
    <line x1="16" y1="3" x2="16" y2="7" />
  </svg>
);

const navIcons = {
  IconLeft: () => <Chevron dir="left" />,
  IconRight: () => <Chevron dir="right" />,
};

/**
 * The app's date picker.
 *
 * Popover by default — a col-md-3 form cell and the .listing-filters row
 * cannot carry ~340px of permanent calendar. Pass `inline` for the always-open
 * bordered box on screens that can.
 *
 *   value / onChange       a Date, or null. Callers keep formatting for the
 *                          payload with Utils.momentDate as they already do.
 *   withTime               adds a divider and a TimeField under the grid
 *   time / onTimeChange    that field's "HH:MM:SS" value, kept separate from
 *                          `value` because AddLeave binds two times to one date
 *   invalid                red border only; the FieldError span stays the
 *                          caller's, as in every other form in this app
 */
const DateField = ({
  value = null,
  onChange,
  withTime = false,
  time = "",
  onTimeChange,
  inline = false,
  dateFormat = STRING.SLASH_DATE_FORMAT,
  placeholder = "Select date",
  disabled = false,
  minDate,
  maxDate,
  invalid = false,
  id,
  className = "",
}) => {
  const generatedId = useId();
  const fieldId = id || "df" + generatedId;
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  // As a popover the panel is a dropdown surface (two steps up, shadow pinned
  // to 3); inline it is part of the page and stays as it was.
  const substrate = useSurface();
  const level = Math.min(substrate + 2, 8);

  /* Sidebar.js:195 dismisses its menus on document mousedown. Match it rather
     than introduce a second dismissal idiom in the same app. */
  useEffect(() => {
    if (!open) return undefined;

    const onDocMouseDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const handleSelect = (date) => {
    if (onChange) onChange(date || null);
    if (!inline) setOpen(false);
  };

  const panel = (
    <div className={"df-panel" + (inline ? "" : " df-panel-pop " + surfaceClasses(level, 3))}>
      <SurfaceProvider value={inline ? substrate : level}>
      <DayPicker
        mode="single"
        selected={value || undefined}
        defaultMonth={value || undefined}
        onSelect={handleSelect}
        fromDate={minDate}
        toDate={maxDate}
        showOutsideDays
        classNames={calendarClassNames}
        components={navIcons}
      />
      {withTime ? (
        <div className="df-panel-time">
          <label className="df-panel-time-label" htmlFor={fieldId + "-time"}>
            Enter time
          </label>
          <TimeField
            id={fieldId + "-time"}
            value={time}
            onChange={onTimeChange}
            disabled={disabled}
          />
        </div>
      ) : null}
      </SurfaceProvider>
    </div>
  );

  const wrapperClass = "df" + (inline ? " df-inline" : "") + (className ? " " + className : "");

  if (inline) {
    return <div className={wrapperClass}>{panel}</div>;
  }

  return (
    <div className={wrapperClass} ref={wrapRef}>
      <button
        type="button"
        id={fieldId}
        className={"df-trigger" + (invalid ? " is-invalid" : "")}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
      >
        <CalendarGlyph />
        <span className={value ? "df-trigger-value" : "df-trigger-placeholder"}>
          {value ? Utils.momentDate(value, dateFormat) : placeholder}
        </span>
      </button>
      {open ? <div className="df-pop">{panel}</div> : null}
    </div>
  );
};

export default DateField;
